import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Camera, 
  Calendar, 
  Image, 
  CreditCard, 
  Shield, 
  FileSignature,
  Tag,
  Users,
  Check,
  X,
  ArrowRight,
  Star,
  Zap,
  Globe,
  Clock,
  Mail,
  ChevronDown,
  Play,
  Loader2,
  ShoppingBag
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { API } from "../App";

// Feature icon mapping
const FEATURE_ICONS = {
  calendar_sync: Calendar,
  watermarking: Shield,
  promo_codes: Tag,
  contracts_releases: FileSignature,
  sell_downloads: CreditCard,
  public_event_galleries: Image,
  invoicing: CreditCard,
  quote_templates: FileSignature,
  portfolio_site: Globe,
  booking_requests: Users,
  availability_calendar: Calendar,
  basic_email_notifications: Mail,
  private_client_galleries_30_day: Image,
  branding_controls: Zap,
  priority_support: Star,
  multi_photographer: Users,
  analytics_reporting: Clock,
  email_templates_automation: Mail
};

const FeaturesPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ show_testimonials: false });
  const [plans, setPlans] = useState([]);
  const [allFeatures, setAllFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch public site settings and plans
    Promise.all([
      axios.get(`${API}/admin/site-settings/public`).catch(() => ({ data: {} })),
      axios.get(`${API}/billing/plans`).catch(() => ({ data: [] }))
    ]).then(([settingsRes, plansRes]) => {
      setSiteSettings(settingsRes.data);
      
      // Filter to only show plans that should be on pricing page
      const visiblePlans = (plansRes.data || [])
        .filter(p => p.show_on_pricing_page !== false && !p.admin_only)
        .sort((a, b) => (a.monthly_price || 0) - (b.monthly_price || 0));
      setPlans(visiblePlans);
      
      // Build feature lookup from plan feature_details (no auth needed)
      const featureLookup = {};
      (plansRes.data || []).forEach(plan => {
        (plan.feature_details || []).forEach(f => {
          if (!featureLookup[f.key]) {
            featureLookup[f.key] = { name: f.name, description: f.description };
          }
        });
      });
      setAllFeatures(featureLookup);
      setLoading(false);
    });
  }, []);

  const heroFeatures = [
    "Online Booking System",
    "Client Galleries",
    "Payment Processing",
    "Contract Signing"
  ];

  // Dynamic main features based on what's implemented
  const getMainFeatures = () => {
    const features = [
      {
        icon: <Calendar className="w-8 h-8" />,
        key: "calendar_sync",
        title: "Smart Scheduling",
        description: "Sync with Google Calendar, set availability, and let clients book online. Never double-book again.",
        features: ["Two-way Google Calendar sync", "Custom availability slots", "Auto-block on confirmation", "Time zone support"],
        tier: "Pro+"
      },
      {
        icon: <Image className="w-8 h-8" />,
        key: "private_client_galleries_30_day",
        title: "Gallery Delivery",
        description: "Complete service fulfillment: create quotes, collect deposits, send final invoices, and unlock gallery access for clients.",
        features: ["Private magic links", "Quote & deposit collection", "Final invoice payment", "Mobile-optimized viewing"],
        tier: "All Plans"
      },
      {
        icon: <Shield className="w-8 h-8" />,
        key: "watermarking",
        title: "Photo Protection",
        description: "Protect your work with custom watermarks. Text or logo, you choose the style.",
        features: ["Text watermarks", "Logo upload", "9 position options", "Adjustable opacity"],
        tier: "Pro+"
      },
      {
        icon: <CreditCard className="w-8 h-8" />,
        key: "invoicing",
        title: "Invoice Automation",
        description: "Reusable invoice templates and automation for recurring clients. Basic invoicing included in all plans.",
        features: ["Reusable templates", "Automated reminders", "Recurring billing", "Promotional pricing"],
        tier: "Pro+"
      },
      {
        icon: <CreditCard className="w-8 h-8" />,
        key: "sell_downloads",
        title: "Sell Photo Downloads",
        description: "Earn more by selling digital downloads from public galleries. Per-photo or package pricing.",
        features: ["Per-photo pricing", "Full gallery packages", "Secure download links", "94% revenue (Pro)"],
        tier: "Pro+"
      },
      {
        icon: <Mail className="w-8 h-8" />,
        key: "email_templates_automation",
        title: "Email Templates",
        description: "Automate client communications with reusable templates and smart triggers.",
        features: ["Reusable templates", "Variable substitution", "Automated triggers", "Preview & test"],
        tier: "Pro+"
      },
      {
        icon: <FileSignature className="w-8 h-8" />,
        key: "contracts_releases",
        title: "Contracts & E-Signs",
        description: "Send service agreements and model releases. Get legally binding signatures digitally.",
        features: ["Template library", "Custom contracts", "E-signatures", "Instant notifications"],
        tier: "Studio"
      },
      {
        icon: <Clock className="w-8 h-8" />,
        key: "analytics_reporting",
        title: "Analytics & Reporting",
        description: "Track your business performance with detailed insights and exportable reports.",
        features: ["Revenue tracking", "Conversion funnel", "Top clients & events", "CSV export"],
        tier: "Studio"
      },
      {
        icon: <Tag className="w-8 h-8" />,
        key: "promo_codes",
        title: "Promo Codes",
        description: "Run promotions to fill your calendar. Percentage or fixed discounts, your rules.",
        features: ["Percentage or fixed", "Usage limits", "Expiry dates", "Event-specific codes"],
        tier: "Pro+"
      }
    ];

    // Mark features as implemented based on backend data
    return features.map(f => ({
      ...f,
      implemented: allFeatures[f.key]?.implemented ?? true
    }));
  };

  // Get plan tier for display
  const getPlanTier = (planKey) => {
    const order = { starter: 1, pro: 2, studio: 3 };
    return order[planKey] || 0;
  };

  // Check if a plan is the most popular (middle tier)
  const isPopularPlan = (plan, allPlans) => {
    if (allPlans.length === 3) {
      return getPlanTier(plan.key) === 2; // Pro is popular
    }
    return false;
  };

  // Get features not in a plan (for "not included" display)
  const getMissingFeatures = (plan, allPlans) => {
    // Get highest tier plan's features
    const highestPlan = allPlans.reduce((max, p) => 
      getPlanTier(p.key) > getPlanTier(max.key) ? p : max
    , allPlans[0]);
    
    const planFeatures = new Set(plan.features || []);
    const missing = (highestPlan.features || [])
      .filter(f => !planFeatures.has(f))
      .slice(0, 4); // Limit to 4 items
    
    return missing.map(f => allFeatures[f]?.name || f);
  };

  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Wedding Photographer",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
      quote: "360Lumina cut my admin time in half. Clients book themselves, contracts get signed automatically, and I get paid faster.",
      rating: 5
    },
    {
      name: "Marcus Chen",
      role: "Sports Photographer",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
      quote: "The public galleries feature is a game-changer for sports events. Parents find and buy their kids' photos easily.",
      rating: 5
    },
    {
      name: "Elena Rodriguez",
      role: "Portrait Studio Owner",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop",
      quote: "Finally, a platform that understands photographers. The watermarking and contracts features give me peace of mind.",
      rating: 5
    }
  ];

  const faqs = [
    {
      q: "How does the free trial work?",
      a: "You get 14 days of full Pro features, no credit card required. After the trial, choose the plan that fits your needs or continue with our Starter plan."
    },
    {
      q: "Can clients book without creating an account?",
      a: "Yes! Clients can book, view galleries, and sign contracts without creating an account. We use secure magic links for access."
    },
    {
      q: "How do I get paid?",
      a: "We use Stripe Connect. Connect your bank account, and payments go directly to you. We never hold your money."
    },
    {
      q: "Are the e-signatures legally binding?",
      a: "Yes. Our e-signatures comply with ESIGN and UETA laws. Each signature includes timestamp, IP address, and agreement confirmation."
    },
    {
      q: "Can I use my own domain?",
      a: "Custom domains are coming soon! For now, you get a professional URL at 360lumina.com/photographer/your-name."
    },
    {
      q: "What happens to my photos if I cancel?",
      a: "Your photos remain accessible for 30 days after cancellation. You can download everything before then. We never delete without warning."
    }
  ];

  const mainFeatures = getMainFeatures();

  return (
    <div className="min-h-screen bg-background" data-testid="features-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Camera className="w-6 h-6 text-primary" />
            <span className="font-serif text-xl">360Lumina</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
            {siteSettings.show_testimonials && (
              <a href="#testimonials" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Reviews</a>
            )}
            <a href="#faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm">Log In</Button>
            </Link>
            <Link to="/pricing">
              <Button size="sm">Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">
              Built by photographers, for photographers
            </Badge>
            <h1 className="text-5xl md:text-7xl font-serif mb-6 leading-tight">
              Run Your Photography Business,{" "}
              <span className="text-primary">Not Around It</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Bookings, galleries, payments, contracts — all in one place. 
              Spend less time on admin, more time behind the camera.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link to="/pricing">
                <Button size="lg" className="text-lg px-8">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              {/* Watch Demo button - hidden until video is uploaded
              <Button variant="outline" size="lg" className="text-lg px-8">
                <Play className="w-5 h-5 mr-2" />
                Watch Demo
              </Button>
              */}
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              {heroFeatures.map((feature, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  {feature}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif mb-4">Everything You Need</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From first inquiry to final delivery, 360Lumina handles every step of your client workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainFeatures.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-card border-border h-full hover:border-primary/50 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 bg-primary/10 rounded-lg text-primary">
                        {feature.icon}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {feature.tier}
                      </Badge>
                    </div>
                    <h3 className="text-xl font-serif mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground mb-4">{feature.description}</p>
                    <ul className="space-y-2">
                      {feature.features.map((f, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-serif mb-2">Save 5+ Hours/Week</h3>
              <p className="text-muted-foreground">
                Automate bookings, contracts, and gallery delivery. Focus on what you do best.
              </p>
            </div>
            <div className="p-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-serif mb-2">Get Paid 2x Faster</h3>
              <p className="text-muted-foreground">
                Embedded checkout and professional invoices mean less chasing, more earning.
              </p>
            </div>
            <div className="p-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-serif mb-2">Look Professional</h3>
              <p className="text-muted-foreground">
                Custom portfolio, branded galleries, and digital contracts elevate your brand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Pricing */}
      <section id="pricing" className="py-20 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif mb-4">Simple, Transparent Pricing</h2>
            <p className="text-lg text-muted-foreground">
              Start free. Upgrade when you are ready. No hidden fees.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan, i) => {
                const popular = isPopularPlan(plan, plans);
                const missingFeatures = getMissingFeatures(plan, plans);
                const isStudio = plan.key === 'studio';
                const isEarlyAccess = plan.is_early_access;
                const showPrice = plan.show_price !== false;
                
                return (
                  <motion.div
                    key={plan.plan_id || i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className={`relative h-full ${popular ? 'border-primary ring-2 ring-primary/20' : 'border-border'} ${isStudio ? 'border-amber-500/30' : ''}`}>
                      {popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
                        </div>
                      )}
                      {isEarlyAccess && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-purple-500/80 text-white flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            Early Access
                          </Badge>
                        </div>
                      )}
                      <CardContent className="p-6">
                        <h3 className="text-xl font-serif mb-1">{plan.name}</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          {plan.key === 'starter' && 'Perfect for getting started'}
                          {plan.key === 'pro' && 'For growing photographers'}
                          {plan.key === 'studio' && 'Built for growing teams'}
                        </p>
                        <div className="mb-6">
                          {isStudio && !showPrice ? (
                            <div>
                              <p className="text-muted-foreground">Contact us for</p>
                              <p className="text-lg text-amber-400 font-medium">early access pricing</p>
                            </div>
                          ) : (
                            <>
                              <span className="text-4xl font-serif">${plan.monthly_price}</span>
                              <span className="text-muted-foreground">/month</span>
                            </>
                          )}
                        </div>
                        {isStudio && isEarlyAccess ? (
                          <Link to="/studio-contact">
                            <Button className="w-full mb-6 bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:bg-amber-500/30" variant="outline">
                              Talk to us
                            </Button>
                          </Link>
                        ) : (
                          <Link to="/pricing">
                            <Button className="w-full mb-6" variant={popular ? "default" : "outline"}>
                              Start Free Trial
                            </Button>
                          </Link>
                        )}
                        
                        {/* Features included */}
                        <ul className="space-y-3 mb-4">
                          {(plan.feature_details || []).slice(0, 8).map((feature, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                              {feature.name}
                            </li>
                          ))}
                          {(plan.feature_details || []).length > 8 && (
                            <li className="text-sm text-muted-foreground pl-6">
                              +{(plan.feature_details || []).length - 8} more features
                            </li>
                          )}
                        </ul>
                        
                        {/* Features not included */}
                        {missingFeatures.length > 0 && (
                          <div className="pt-4 border-t border-border">
                            <p className="text-xs text-muted-foreground mb-2">Not included:</p>
                            <ul className="space-y-1">
                              {missingFeatures.map((f, j) => (
                                <li key={j} className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <X className="w-3 h-3" />
                                  {f}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Paid Add-Ons Section */}
      <section className="py-20 px-4" data-testid="features-addons-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif mb-4">Paid Add-Ons</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Expand your business with powerful additions to your plan
            </p>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Card className="bg-card border-border max-w-2xl mx-auto">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <ShoppingBag className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-serif">Merchandise Store</h3>
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                        Coming Soon
                      </Badge>
                    </div>
                    <p className="text-muted-foreground mb-4">
                      Sell prints, albums, and custom products directly to your clients. 
                      Powered by print-on-demand integration.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-2 mb-4">
                      {[
                        "Print-on-demand integration",
                        "Custom product catalog",
                        "Inventory management",
                        "Client merchandise page"
                      ].map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Available for:</span>
                      <Badge variant="outline" className="text-xs">Pro</Badge>
                      <Badge variant="outline" className="text-xs">Studio</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Testimonials - Conditionally shown based on admin settings */}
      {siteSettings.show_testimonials && (
        <section id="testimonials" className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-serif mb-4">Loved by Photographers</h2>
              <p className="text-lg text-muted-foreground">
                Join thousands of photographers who have simplified their business.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="bg-card border-border h-full">
                    <CardContent className="p-6">
                      <div className="flex gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, j) => (
                          <Star key={j} className="w-4 h-4 fill-primary text-primary" />
                        ))}
                      </div>
                      <p className="text-foreground mb-6 italic">&quot;{testimonial.quote}&quot;</p>
                      <div className="flex items-center gap-3">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-medium">{testimonial.name}</p>
                          <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 bg-card/30">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <Card 
                key={i} 
                className="bg-card border-border cursor-pointer"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">{faq.q}</h3>
                    <ChevronDown className={`w-5 h-5 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </div>
                  {openFaq === i && (
                    <p className="text-muted-foreground mt-4">{faq.a}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-serif mb-4">Ready to Simplify Your Business?</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Start your free 14-day trial. No credit card required.
          </p>
          <Link to="/pricing">
            <Button size="lg" className="text-lg px-8">
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              <span className="font-serif">360Lumina</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
              <a href="#" className="hover:text-foreground transition-colors">Terms</a>
              <Link to="/help" className="hover:text-foreground transition-colors">Support</Link>
              <a href="mailto:support@360lumina.com" className="hover:text-foreground transition-colors">Contact</a>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} 360Lumina. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
